/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: GPT-5 Codex
 🏢 Company: Ring2All LLC
 📄 File: hooks/useCdrRecordList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing CDR Records list.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useState, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchCdrRecords } from '../api';
import { CdrRecord, ListCdrRecordsParams, CdrFilterValues } from '../types';

interface UseCdrRecordListProps {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing CDR Records list
 */
export function useCdrRecordList(props: UseCdrRecordListProps = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  // Filter state
  const [filters, setFilters] = useState<CdrFilterValues>({
    startDate: '',
    endDate: '',
    caller: '',
    destination: '',
    direction: '',
    hangupCause: '',
  });

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const queryParams: ListCdrRecordsParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        // Add filters to query params
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        callerId: filters.caller || undefined,
        destinationNumber: filters.destination || undefined,
        direction: filters.direction || undefined,
        hangupCause: filters.hangupCause || undefined,
        filterId: filters.savedFilterId || undefined,
      };

      const result = await fetchCdrRecords(queryParams, effectiveDomainId);

      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('callDetailRecords.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, filters, effectiveDomainId]);

  const availableColumns = useMemo(() => [
    // 🔹 Core Fields (Visible by Default)
    { key: 'id', label: t('callDetailRecords.columns.id'), visible: false, sortable: true },
    { key: 'uuid', label: t('callDetailRecords.columns.uuid'), visible: false, sortable: true },
    { key: 'tenantId', label: t('callDetailRecords.columns.tenantId'), visible: false, sortable: true },
    { key: 'domainId', label: t('callDetailRecords.columns.domainId'), visible: false, sortable: true },
    { key: 'domainName', label: t('callDetailRecords.columns.domainName'), visible: false, sortable: true },

    // 🔹 Call Participants (Visible by Default)
    { key: 'callerIdName', label: t('callDetailRecords.columns.callerIdName'), visible: false, sortable: true },
    { key: 'callerIdNumber', label: t('callDetailRecords.columns.callerIdNumber'), visible: true, sortable: true },
    { key: 'destinationNumber', label: t('callDetailRecords.columns.destinationNumber'), visible: true, sortable: true },

    // 🔹 Call Routing (Visible by Default)
    { key: 'direction', label: t('callDetailRecords.columns.direction'), visible: true, sortable: true },
    { key: 'context', label: t('callDetailRecords.columns.context'), visible: false, sortable: true },

    // 🔹 Timing Information (Visible by Default)
    { key: 'startStamp', label: t('callDetailRecords.columns.startStamp'), visible: true, sortable: true },
    { key: 'answerStamp', label: t('callDetailRecords.columns.answerStamp'), visible: false, sortable: true },
    { key: 'endStamp', label: t('callDetailRecords.columns.endStamp'), visible: false, sortable: true },
    { key: 'duration', label: t('callDetailRecords.columns.duration'), visible: true, sortable: true },
    { key: 'billsec', label: t('callDetailRecords.columns.billsec'), visible: true, sortable: true },
    { key: 'pddMs', label: t('callDetailRecords.columns.pddMs'), visible: false, sortable: true },

    // 🔹 Call Disposition (Visible by Default)
    { key: 'hangupCause', label: t('callDetailRecords.columns.hangupCause'), visible: true, sortable: true },
    { key: 'sipHangupDisposition', label: t('callDetailRecords.columns.sipHangupDisposition'), visible: false, sortable: true },

    // 🔹 Bridging & Routing
    { key: 'bridgeUuid', label: t('callDetailRecords.columns.bridgeUuid'), visible: false, sortable: true },

    // 🔹 Billing
    { key: 'accountcode', label: t('callDetailRecords.columns.accountcode'), visible: false, sortable: true },
    { key: 'authorizationCode', label: t('callDetailRecords.columns.authorizationCode'), visible: false, sortable: true },

    // 🔹 Gateway/Routing
    { key: 'gateway', label: t('callDetailRecords.columns.gateway'), visible: true, sortable: true },

    // 🔹 Quality Metrics (Available)
    { key: 'rtpAudioInMos', label: t('callDetailRecords.columns.rtpAudioInMos'), visible: false, sortable: true },
    { key: 'rtpAudioInPacketLossPercent', label: t('callDetailRecords.columns.rtpAudioInPacketLossPercent'), visible: false, sortable: true },
    { key: 'rtpAudioInJitterMean', label: t('callDetailRecords.columns.rtpAudioInJitterMean'), visible: false, sortable: true },
    { key: 'rtpAudioInLargestDelta', label: t('callDetailRecords.columns.rtpAudioInLargestDelta'), visible: false, sortable: true },

    // 🔸 Network & Media (Available)
    { key: 'localIpV4', label: t('callDetailRecords.columns.localIpV4'), visible: false, sortable: true },
    { key: 'remoteMediaIp', label: t('callDetailRecords.columns.remoteMediaIp'), visible: false, sortable: true },
    { key: 'networkAddr', label: t('callDetailRecords.columns.networkAddr'), visible: false, sortable: true },
    { key: 'readCodec', label: t('callDetailRecords.columns.readCodec'), visible: false, sortable: true },
    { key: 'writeCodec', label: t('callDetailRecords.columns.writeCodec'), visible: false, sortable: true },

    // 🔸 Application Details (Available)
    { key: 'lastApp', label: t('callDetailRecords.columns.lastApp'), visible: false, sortable: true },
    { key: 'lastArg', label: t('callDetailRecords.columns.lastArg'), visible: false, sortable: true },

    // 🔸 Recording (Available)
    { key: 'recordingFile', label: t('callDetailRecords.columns.recordingFile'), visible: false, sortable: true },

    // 🔸 Metadata (Available)
    { key: 'insertDate', label: t('callDetailRecords.columns.insertDate'), visible: false, sortable: true },
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'startStamp',
    initialSortOrder: 'desc',
    initialLimit: 25,
    moduleName: 'callDetailRecords',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  // Filter handlers
  const handleFilterChange = useCallback((newFilters: CdrFilterValues) => {
    setFilters(newFilters);
    // NO auto-refresh - user must click Apply button
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      startDate: '',
      endDate: '',
      caller: '',
      destination: '',
      direction: '',
      hangupCause: '',
    });
    // Auto-refresh when clearing filters
    setTimeout(() => refresh(), 100);
  }, [refresh]);

  const handleApplyFilters = useCallback(() => {
    refresh();
  }, [refresh]);

  return {
    data: data as CdrRecord[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    refresh,
    effectiveDomainId,
  };
}

